import type { DatesType } from '@/app/lib/types'

/** Entrada normalizada, independiente del nombre de campo de cada API (postventa/
 * `/data` usan `start_date`/`end_date`; la futura PDP usará `start_at`/`end_at`) —
 * ver contracts/formatEventDate.md. */
export interface EventDateInput {
  start: string | null
  end: string | null
  datesType?: DatesType | null
  /** Texto de fecha editorial desde el CMS (FR-014/FR-015). Con contenido (tras
   * `.trim()`) prevalece sobre `datesType`/`start`/`end`, devuelto verbatim. */
  dateNotVisible?: string | null
}

export interface DateParts {
  day: string
  month: string
  year: string
}

const DATE_SEGMENT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/

/** Extrae `YYYY-MM-DD` del inicio de la cadena (solo-fecha o ISO datetime con
 * offset) sin construir un `Date` ni convertir zona horaria — evita el
 * desplazamiento de día que produciría `Date`/`Intl.DateTimeFormat` en fechas-hora
 * cercanas a medianoche (specs/017-normalizacion-fechas-eventos/research.md §1).
 *
 * Exportada para que `isEventPast` compare el día del evento con el mismo
 * criterio que usa este resolutor para pintarlo, en vez de añadir una tercera
 * copia de la expresión regular (la otra vive en `formatPurchaseDate`). */
export function extractDateParts(value: string | null): DateParts | null {
  if (!value) return null
  const match = DATE_SEGMENT_PATTERN.exec(value)
  if (!match) return null
  const [, year, month, day] = match
  if (!year || !month || !day) return null
  return { year, month, day }
}

function partsEqual(a: DateParts, b: DateParts): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day
}

/**
 * Resultado de `resolveEventDateCase` — decide QUÉ caso de fecha aplica, sin
 * decidir todavía el texto final: cada formateador (`formatEventDate`,
 * `formatEventDateTabla` de `ProductosTabla`) traduce estos casos a su propio
 * texto (specs/037-productos-tabla/data-model.md §5).
 */
export type EventDateCase =
  | { kind: 'literal'; text: string }
  | { kind: 'pronto' }
  | { kind: 'single'; date: DateParts }
  | { kind: 'open-start'; start: DateParts }
  | { kind: 'open-end'; end: DateParts }
  | { kind: 'closed-range'; start: DateParts; end: DateParts }

/**
 * Resolutor puro y compartido de la decisión de fechas (FR-001 a FR-013 de
 * specs/017-normalizacion-fechas-eventos): qué caso aplica según
 * `date_not_visible`/`dates_type`/`start`/`end`, sin decidir el formato de
 * salida. Extraído de `formatEventDate` para que un segundo consumidor
 * (`ProductosTabla`) reutilice la misma decisión de negocio en vez de
 * duplicar este switch (specs/037-productos-tabla/research.md §1).
 */
export function resolveEventDateCase(input: EventDateInput): EventDateCase {
  if (input.dateNotVisible?.trim()) return { kind: 'literal', text: input.dateNotVisible }

  const start = extractDateParts(input.start)
  const end = extractDateParts(input.end)

  switch (input.datesType) {
    case 'one-day':
      return start ? { kind: 'single', date: start } : { kind: 'pronto' }

    case 'from':
      if (!start) return { kind: 'pronto' }
      if (end && partsEqual(start, end)) return { kind: 'single', date: start }
      return { kind: 'open-start', start }

    case 'until':
      if (!end) return { kind: 'pronto' }
      if (start && partsEqual(start, end)) return { kind: 'single', date: end }
      return { kind: 'open-end', end }

    case 'range':
    default:
      // `end` ausente con `start` presente: un rango sin fecha de fin aún no
      // cerrada se trata igual que `from` — `start` sigue siendo una fecha
      // concreta y usable, a diferencia de cuando falta `start` (ese caso sí
      // sigue siendo "próximamente": sin fecha de inicio no hay nada que
      // mostrar). Ver specs/017-normalizacion-fechas-eventos.
      if (start && !end) return { kind: 'open-start', start }
      if (!start || !end) return { kind: 'pronto' }
      if (partsEqual(start, end)) return { kind: 'single', date: start }
      return { kind: 'closed-range', start, end }
  }
}
